import React from 'react';
import './PrivacyPolicy.css';

const PrivacyPolicy = () => {
  return (
    <div className="policy-page">
      <div className="policy-header">
        <div className="container">
          <h1>Privacy Policy</h1>
          <p>Last updated: April 27, 2026</p>
        </div>
      </div>

      <div className="container policy-content my-5">
        <p className="intro">
          This Privacy Policy explains how Thrive Insurance Brokers Limited  collects, uses, stores, shares, and protects personal information when you visit our website, use our services, or interact with our communications.
        </p>

        <section>
          <h2>Scope of This Policy</h2>
          <p>
            This policy applies to all personal data collected through our website, customer service channels, insurance quote requests, claims submissions, contact forms, and any other interactions where Thrive Insurance Brokers Limited collects personal information.
          </p>
        </section>

        <section>
          <h2>Information We Collect</h2>
          <p>
            We collect several types of information in order to provide and improve our insurance brokerage services. This includes:
          </p>
          <ul>
            <li><strong>Identity Information:</strong> full name (collected through contact and claim forms).</li>
            <li><strong>Contact Information:</strong> email address, phone number, and other contact details you provide through our contact form, newsletter subscription, and claim reporting.</li>
            <li><strong>Insurance Information:</strong> policy number, insured name, claims information, and documentation provided during claim submissions.</li>
            <li><strong>Transactional Information:</strong> information related to insurance inquiries, claims submissions, contact form submissions, newsletter subscriptions, and communications with Thrive.</li>
            <li><strong>Technical Information:</strong> IP address, device type, browser information, operating system, pages visited, time spent on pages, referral source, and other usage analytics collected through cookies and web server logs.</li>
            <li><strong>Marketing Information:</strong> email address for newsletter subscriptions and preferences for receiving communications about insurance products.</li>
            <li><strong>Claims Documentation:</strong> supporting documents and files uploaded during the claim reporting process.</li>
          </ul>
        </section>

        <section>
          <h2>How We Collect Information</h2>
          <p>
            We collect information in the following ways:
          </p>
          <ul>
            <li><strong>Through Contact Forms:</strong> when you submit inquiries via our "Contact Us" page, providing name, email, phone, and message.</li>
            <li><strong>Through Newsletter Subscription:</strong> when you subscribe to our newsletter by providing your email address.</li>
            <li><strong>Through Claim Reporting:</strong> when you submit a claim by providing insured name, policy number, email, phone, and supporting documents.</li>
            <li><strong>Automatically via Cookies and Analytics:</strong> when you browse our website, we collect technical data including IP address, device identifiers, browser type, operating system, pages visited, and navigation patterns through cookies and web analytics.</li>
            <li><strong>Through Cookie Preferences:</strong> when you set your cookie preferences, we store your consent preferences.</li>
            <li><strong>From third parties:</strong> such as service providers, analytics providers, or public data when permitted by law.</li>
          </ul>
        </section>

        <section>
          <h2>Why We Use Your Information</h2>
          <p>
            We use your personal information for purposes that include:
          </p>
          <ul>
            <li>Processing and responding to contact form submissions and customer inquiries.</li>
            <li>Managing newsletter subscriptions and delivering insurance-related updates and offers.</li>
            <li>Processing insurance claims, including verifying information and managing supporting documentation.</li>
            <li>Providing, operating, and maintaining our website and insurance brokerage services.</li>
            <li>Communicating with you about your inquiries, claims, or subscriptions.</li>
            <li>Analyzing website traffic, user behavior, and usage patterns to improve website performance and user experience.</li>
            <li>Personalizing content and recommendations based on your interests and browsing behavior.</li>
            <li>Protecting against fraud, abuse, and security threats in insurance transactions and website usage.</li>
            <li>Complying with insurance regulations, NDPR requirements, legal obligations, resolving disputes, and enforcing our policies.</li>
            <li>Maintaining records for claims management and regulatory compliance.</li>
          </ul>
        </section>

        <section>
          <h2>Legal Basis for Processing</h2>
          <p>
            Where applicable, we process your personal data based on several lawful grounds, including your consent, our legitimate interests (such as improving our services and protecting our systems), and our obligations to comply with applicable laws.
          </p>
        </section>

        <section>
          <h2>How We Share Your Information</h2>
          <p>
            We do not sell your personal information. We may share your information with:
          </p>
          <ul>
            <li><strong>Service providers:</strong> companies that help us operate our website, process form submissions, send newsletter communications, host data, or provide technical support.</li>
            <li><strong>Insurance underwriters and claims partners:</strong> insurance companies and third-party claims processors who assist in processing insurance claims and policy management.</li>
            <li><strong>Payment processors:</strong> third-party services that may process payments related to insurance transactions.</li>
            <li><strong>Analytics providers:</strong> third-party analytics services that help us understand website usage and improve our services.</li>
            <li><strong>Legal and regulatory authorities:</strong> when required to comply with insurance regulations (NAICOM, NCRIB), data protection laws (NDPR), or respond to lawful government requests.</li>
            <li><strong>Business transfers:</strong> in the event of a merger, acquisition, or sale of assets, where personal information may be transferred as part of the business transaction.</li>
          </ul>
        </section>

        <section>
          <h2>International Data Transfers</h2>
          <p>
            We may transfer your personal data to service providers or partners located in countries outside your jurisdiction. When we do so, we take steps to ensure appropriate safeguards are in place to protect your information in accordance with applicable data protection laws.
          </p>
        </section>

        <section>
          <h2>Data Retention</h2>
          <p>
            We retain your personal information only as long as necessary to fulfill the purposes for which it was collected, comply with legal obligations, resolve disputes, and enforce agreements. Retention periods may vary depending on the type of information and the purpose for processing.
          </p>
        </section>

        <section>
          <h2>Data Accuracy and Insurance Underwriting</h2>
          <p>
            For insurance services, the accuracy of personal data is critical to:
          </p>
          <ul>
            <li><strong>Underwriting:</strong> Insurance underwriters rely on accurate information to assess risk and determine appropriate coverage and premiums.</li>
            <li><strong>Claims Processing:</strong> Accurate information helps ensure that claims are processed correctly and in accordance with policy terms.</li>
            <li><strong>Regulatory Compliance:</strong> Accurate data is necessary to comply with insurance regulations and anti-fraud measures.</li>
          </ul>
          <p>
            You agree to provide accurate, complete, and truthful information. Misrepresentation, material omission, or fraudulent information in insurance applications, claims, or disclosures may result in policy cancellation, claim denial, or legal action. Changes in circumstances that affect your insurance coverage should be reported promptly.
          </p>
        </section>

        <section>
          <h2>Data Security</h2>
          <p>
            We use reasonable and appropriate technical, administrative, and organizational measures to protect your personal data from unauthorized access, disclosure, alteration, or destruction. While we strive to protect your information, no method of transmission or storage is completely secure.
          </p>
        </section>

        <section>
          <h2>Your Rights</h2>
          <p>
            Depending on your jurisdiction, you may have certain rights regarding your personal information, including the right to:
          </p>
          <ul>
            <li>Access the data we hold about you.</li>
            <li>Correct or update inaccurate information.</li>
            <li>Delete your personal data where permitted.</li>
            <li>Restrict or object to certain processing activities.</li>
            <li>Withdraw consent where processing is based on consent.</li>
          </ul>
          <p>
            To exercise these rights, please contact us using the details provided below.
          </p>
        </section>

        <section>
          <h2>Children's Privacy</h2>
          <p>
            Our website is not intended for children under the age of 13. We do not knowingly collect personal information from children without parental consent. If we learn that we have collected personal information from a child without appropriate consent, we will take steps to delete it.
          </p>
        </section>

        <section>
          <h2>Cookies and Tracking</h2>
          <p>
            We use cookies and similar technologies to collect information about your interactions with our website. For more details, please refer to our <a href="/cookie-policy">Cookie Policy</a>.
          </p>
        </section>

        <section>
          <h2>Changes to This Policy</h2>
          <p>
            We may update this Privacy Policy periodically to reflect changes in our practices, technology, or legal requirements. When we make material changes, we will post the updated policy on this page and revise the "Last updated" date.
          </p>
        </section>

        <section>
          <h2>Contact Us</h2>
          <p>
            If you have questions about this Privacy Policy, wish to exercise your rights, or have concerns regarding your insurance information, please contact us through our <a href="/contact">Contact Us</a> page.
          </p>
        </section>
      </div>
    </div>
  );
};

export default PrivacyPolicy;